import { type Accessor, createEffect, createMemo, createSignal, on, onCleanup } from "solid-js"
import { localMode } from "@/transport/local-mode"
import { listAccountWorkspaces, type InventoryWorkspace } from "@/transport/workspace-inventory"
import { createQuery } from "@/utils/query"

const [revision, setRevision] = createSignal(0)

/** A machine reported its workspaces, or the account stream (re)connected and may have missed a report. */
export function invalidateWorkspaceInventory() {
  setRevision((value) => value + 1)
}

export type WorkspaceInventory = {
  /** The account has answered once, or failed to, or there is no account copy to wait for; a later reload never unsettles it. */
  settled: Accessor<boolean>
  /** Reads the account's copy again, as pull-to-refresh asks. */
  refresh: () => Promise<unknown>
  /** The machine's last reported open workspaces, in its order; undefined before the account answers or without a report. */
  workspaces: (machine: string) => readonly InventoryWorkspace[] | undefined
  /** Every reported workspace of the machine, closed ones included. */
  all: (machine: string) => readonly InventoryWorkspace[]
}

/**
 * The account's copy of every machine's workspaces, so the sidebar can list an
 * offline machine's workspaces and place a machine's rows before it connects.
 * It reloads whenever a machine reports again and when the page becomes
 * visible. A standalone machine has no account copy: it lists nothing.
 */
export function createWorkspaceInventory(): WorkspaceInventory {
  const reports = createQuery(() => (localMode() ? false : "account"), (_, { signal }) => listAccountWorkspaces(signal))
  createEffect(on(revision, () => void reports.refresh(), { defer: true }))
  const revalidate = () => {
    if (document.visibilityState === "visible") void reports.refresh()
  }
  document.addEventListener("visibilitychange", revalidate)
  onCleanup(() => document.removeEventListener("visibilitychange", revalidate))
  const byMachine = createMemo(() => new Map((reports() ?? []).map((report) => [report.machine, report.workspaces])))
  const open: Accessor<Map<string, InventoryWorkspace[]>> = createMemo(() =>
    new Map([...byMachine()].map(([machine, workspaces]) => [machine, workspaces.filter((workspace) => !workspace.closed)])))
  // Latched: a reload after a failed read clears its error while it runs, and
  // the sidebar must not go back to its skeleton then.
  const settled = createMemo((was: boolean) => was || localMode() || reports() !== undefined || reports.error !== undefined, false)
  return {
    settled,
    refresh: () => reports.refresh(),
    workspaces: (machine) => open().get(machine),
    all: (machine) => byMachine().get(machine) ?? [],
  }
}
